// AchievementsEditor.jsx
import { useState, useEffect } from 'react';
import { get, post, put, del } from '../../utils/api';
import { getUserLanguage } from '../../utils/language';

const AchievementsEditor = () => {
  const [achievements, setAchievements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentAchievement, setCurrentAchievement] = useState({
    id: null,
    year: '',
    display_order: 0,
    is_active: true,
    translations: {
      en: { title: '', description: '' },
      cs: { title: '', description: '' },
    },
  });
  const language = getUserLanguage();

  useEffect(() => {
    fetchAchievements();
  }, [language]);

  const fetchAchievements = async () => {
    setLoading(true);
    setError(null);
    try {
      const headers = { 'accept-language': language };
      const data = await get('/achievements', headers);
      setAchievements(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('[AchievementsEditor] Error fetching achievements:', err.message, err.stack);
      setError(language === 'cs' ? 'Nepodařilo se načíst úspěchy' : 'Failed to load achievements');
      setAchievements([]);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e, lang) => {
    const { name, value, type, checked } = e.target;
    if (['title', 'description'].includes(name)) {
      setCurrentAchievement((prev) => ({
        ...prev,
        translations: {
          ...prev.translations,
          [lang]: { ...prev.translations[lang], [name]: value },
        },
      }));
    } else {
      setCurrentAchievement((prev) => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!currentAchievement.translations.en.title || !currentAchievement.translations.cs.title) {
      setError(language === 'cs' ? 'Název je povinný v obou jazycích' : 'Title is required in both languages');
      return;
    }

    const data = {
      year: currentAchievement.year,
      display_order: currentAchievement.display_order || 0,
      is_active: currentAchievement.is_active ? 'true' : 'false',
      translations: currentAchievement.translations,
    };

    try {
      if (editMode) {
        await put(`/achievements/${currentAchievement.id}`, data);
        setSuccess(language === 'cs' ? 'Úspěch aktualizován' : 'Achievement updated successfully');
      } else {
        await post('/achievements', data);
        setSuccess(language === 'cs' ? 'Úspěch přidán' : 'Achievement added successfully');
      }

      resetForm();
      fetchAchievements();
    } catch (err) {
      console.error('[AchievementsEditor] Error saving achievement:', err.message, err.stack);
      setError(language === 'cs' ? 'Nepodařilo se uložit úspěch' : 'Failed to save achievement');
    }
  };

  const handleEdit = async (id) => {
    try {
      const headersEn = { 'accept-language': 'en' };
      const headersCs = { 'accept-language': 'cs' };
      const [achievementEn, achievementCs] = await Promise.all([
        get(`/achievements/${id}`, headersEn),
        get(`/achievements/${id}`, headersCs),
      ]);

      setCurrentAchievement({
        id: achievementEn.id,
        year: achievementEn.year || '',
        display_order: achievementEn.display_order || 0,
        is_active: achievementEn.is_active !== undefined ? !!achievementEn.is_active : true,
        translations: {
          en: { title: achievementEn.title || '', description: achievementEn.description || '' },
          cs: { title: achievementCs.title || '', description: achievementCs.description || '' },
        },
      });
      setEditMode(true);
      setShowForm(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      console.error('[AchievementsEditor] Error fetching achievement for edit:', err.message, err.stack);
      setError(language === 'cs' ? 'Nepodařilo se načíst úspěch pro úpravu' : 'Failed to load achievement for editing');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(language === 'cs' ? 'Opravdu chcete smazat tento úspěch?' : 'Are you sure you want to delete this achievement?')) return;
    try {
      await del(`/achievements/${id}`);
      setSuccess(language === 'cs' ? 'Úspěch smazán' : 'Achievement deleted successfully');
      fetchAchievements();
    } catch (err) {
      console.error('[AchievementsEditor] Error deleting achievement:', err.message, err.stack);
      setError(language === 'cs' ? 'Nepodařilo se smazat úspěch' : 'Failed to delete achievement');
    }
  };

  const resetForm = () => {
    setCurrentAchievement({
      id: null,
      year: '',
      display_order: 0,
      is_active: true,
      translations: {
        en: { title: '', description: '' },
        cs: { title: '', description: '' },
      },
    });
    setEditMode(false);
    setShowForm(false);
    setError(null);
    setSuccess(null);
  };

  const handleAddNew = () => {
    resetForm();
    setShowForm(true);
  };

  if (loading) {
    return <div className="admin-p-4">{language === 'cs' ? 'Načítání...' : 'Loading...'}</div>;
  }

  return (
    <div className="admin-p-4">
      <div className="admin-flex admin-justify-between admin-items-center admin-mb-6">
        <h1 className="admin-text-2xl admin-font-bold">{language === 'cs' ? 'Správa úspěchů' : 'Achievements Management'}</h1>
        <button onClick={handleAddNew} className="admin-btn admin-btn-primary">
          {language === 'cs' ? 'Přidat úspěch' : 'Add Achievement'}
        </button>
      </div>

      {error && <div className="admin-alert admin-alert-error">{error}</div>}
      {success && <div className="admin-alert admin-alert-success">{success}</div>}

      {showForm && (
        <div className="admin-card admin-mb-8 admin-bg-dark-gray">
          <div className="admin-card-header admin-bg-accent-gray">
            <h2 className="admin-text-xl admin-font-semibold">
              {editMode ? (language === 'cs' ? 'Upravit úspěch' : 'Edit Achievement') : (language === 'cs' ? 'Přidat úspěch' : 'Add Achievement')}
            </h2>
          </div>
          <div className="admin-card-body">
            <form onSubmit={handleSubmit}>
              <div className="admin-grid admin-grid-cols-1 admin-md:grid-cols-2 admin-gap-6">
                <div>
                  <h3 className="admin-text-lg admin-font-medium admin-mb-2">English</h3>
                  <div className="admin-mb-4">
                    <label className="admin-form-label">Title</label>
                    <input
                      type="text"
                      name="title"
                      className="admin-form-input"
                      value={currentAchievement.translations.en.title}
                      onChange={(e) => handleChange(e, 'en')}
                      required
                    />
                  </div>
                  <div className="admin-mb-4">
                    <label className="admin-form-label">Description</label>
                    <textarea
                      name="description"
                      className="admin-form-textarea"
                      value={currentAchievement.translations.en.description}
                      onChange={(e) => handleChange(e, 'en')}
                    />
                  </div>

                  <h3 className="admin-text-lg admin-font-medium admin-mb-2">Čeština</h3>
                  <div className="admin-mb-4">
                    <label className="admin-form-label">Název</label>
                    <input
                      type="text"
                      name="title"
                      className="admin-form-input"
                      value={currentAchievement.translations.cs.title}
                      onChange={(e) => handleChange(e, 'cs')}
                      required
                    />
                  </div>
                  <div className="admin-mb-4">
                    <label className="admin-form-label">Popis</label>
                    <textarea
                      name="description"
                      className="admin-form-textarea"
                      value={currentAchievement.translations.cs.description}
                      onChange={(e) => handleChange(e, 'cs')}
                    />
                  </div>
                </div>

                <div>
                  <div className="admin-mb-4">
                    <label className="admin-form-label">{language === 'cs' ? 'Rok' : 'Year'}</label>
                    <input
                      type="number"
                      name="year"
                      className="admin-form-input"
                      value={currentAchievement.year}
                      onChange={(e) => handleChange(e, language)}
                      required
                    />
                  </div>

                  <div className="admin-mb-4">
                    <label className="admin-form-label">{language === 'cs' ? 'Pořadí zobrazení' : 'Display Order'}</label>
                    <input
                      type="number"
                      name="display_order"
                      className="admin-form-input"
                      value={currentAchievement.display_order}
                      onChange={(e) => handleChange(e, language)}
                    />
                  </div>

                  <div className="admin-mb-4">
                    <label className="admin-flex admin-items-center">
                      <input
                        type="checkbox"
                        name="is_active"
                        className="admin-form-checkbox"
                        checked={currentAchievement.is_active}
                        onChange={(e) => handleChange(e, language)}
                      />
                      <span className="admin-text-white admin-text-sm admin-font-bold">{language === 'cs' ? 'Aktivní' : 'Active'}</span>
                    </label>
                  </div>
                </div>
              </div>

              <div className="admin-mt-6 admin-flex admin-items-center">
                <button type="submit" className="admin-btn admin-btn-primary admin-mr-2">
                  {editMode ? (language === 'cs' ? 'Aktualizovat' : 'Update') : (language === 'cs' ? 'Přidat' : 'Add')}
                </button>
                <button type="button" className="admin-btn admin-btn-secondary" onClick={resetForm}>
                  {language === 'cs' ? 'Zrušit' : 'Cancel'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="admin-card admin-bg-dark-gray">
        <div className="admin-card-header admin-bg-accent-gray">{language === 'cs' ? 'Seznam úspěchů' : 'Achievements List'}</div>
        <div className="admin-overflow-x-auto">
          <table className="admin-table">
            <thead>
              <tr className="admin-bg-accent-gray">
                <th className="admin-px-6 admin-py-3 admin-text-left">{language === 'cs' ? 'Název' : 'Title'}</th>
                <th className="admin-px-6 admin-py-3 admin-text-left">{language === 'cs' ? 'Rok' : 'Year'}</th>
                <th className="admin-px-6 admin-py-3 admin-text-left">{language === 'cs' ? 'Pořadí' : 'Order'}</th>
                <th className="admin-px-6 admin-py-3 admin-text-left">{language === 'cs' ? 'Aktivní' : 'Active'}</th>
                <th className="admin-px-6 admin-py-3 admin-text-left">{language === 'cs' ? 'Akce' : 'Actions'}</th>
              </tr>
            </thead>
            <tbody>
              {achievements.map((achievement) => (
                <tr key={achievement.id || Math.random()} className="admin-table-row">
                  <td className="admin-px-6 admin-py-4 admin-whitespace-nowrap">
                    <div className="admin-text-sm admin-font-medium admin-text-white">
                      {achievement.title || (language === 'cs' ? 'Není název' : 'No title')}
                    </div>
                  </td>
                  <td className="admin-px-6 admin-py-4 admin-whitespace-nowrap">
                    <div className="admin-text-sm admin-text-white">
                      {achievement.year || (language === 'cs' ? 'Není rok' : 'No year')}
                    </div>
                  </td>
                  <td className="admin-px-6 admin-py-4 admin-whitespace-nowrap">
                    <div className="admin-text-sm admin-text-white">{achievement.display_order || '0'}</div>
                  </td>
                  <td className="admin-px-6 admin-py-4 admin-whitespace-nowrap">
                    <span className={`admin-badge ${achievement.is_active ? 'admin-badge-green' : 'admin-badge-red'}`}>
                      {achievement.is_active ? (language === 'cs' ? 'Ano' : 'Yes') : (language === 'cs' ? 'Ne' : 'No')}
                    </span>
                  </td>
                  <td className="admin-px-6 admin-py-4 admin-whitespace-nowrap admin-text-sm admin-font-medium">
                    <button
                      onClick={() => handleEdit(achievement.id)}
                      className="admin-text-blue-600 hover:admin-text-blue-900 admin-mr-3"
                    >
                      {language === 'cs' ? 'Upravit' : 'Edit'}
                    </button>
                    <button
                      onClick={() => handleDelete(achievement.id)}
                      className="admin-text-red-600 hover:admin-text-red-900"
                    >
                      {language === 'cs' ? 'Smazat' : 'Delete'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AchievementsEditor;